<template>
  <section id="products" class="py-12" style="background-color: #f8faff;">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="mb-8 text-center">
        <h3 class="text-2xl font-semibold text-gray-900 mb-2">ผลิตภัณฑ์</h3>
        <p class="text-gray-600">สินค้าและผลิตภัณฑ์คุณภาพดี</p>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="flex justify-center items-center py-12">
        <div class="text-center">
          <div class="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-gray-600 mx-auto mb-2"></div>
          <p class="text-sm text-gray-500">กำลังโหลด...</p>
        </div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="text-center py-12">
        <p class="text-gray-500 mb-3">{{ error }}</p>
        <button 
          @click="fetchProducts" 
          class="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 transition-colors"
        >
          ลองใหม่
        </button>
      </div>

      <!-- Products Content -->
      <div v-else>
        <!-- Products Grid -->
        <div v-if="paginatedProducts.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <div 
            v-for="product in paginatedProducts" 
            :key="product._id"
            class="bg-white border border-gray-200 rounded-lg overflow-hidden hover:border-gray-300 transition-colors shadow-sm"
          >
            <div class="relative h-40 bg-gray-100">
              <img 
                :src="getProductImage(product)" 
                :alt="product.productName" 
                class="w-full h-full object-cover"
                @error="handleImageError"
              >
              <div v-if="getDiscountPercentage(product) > 0" 
                   class="absolute top-3 right-3 bg-red-600 text-white px-2 py-1 rounded text-xs">
                -{{ getDiscountPercentage(product) }}%
              </div>
              
              <!-- Category Badge -->
              <div class="absolute top-3 left-3">
                <span class="text-xs text-gray-600 bg-white bg-opacity-90 px-2 py-1 rounded">
                  {{ product.category || 'สินค้า' }}
                </span>
              </div>
            </div>
            
            <div class="p-4">
              <h4 class="font-medium text-gray-900 mb-2 line-clamp-2">{{ product.productName }}</h4>
              <p class="text-sm text-gray-600 mb-3 line-clamp-2">
                {{ product.description || 'ผลิตภัณฑ์คุณภาพจากเครือข่ายคลัสเตอร์ฮาลาล' }}
              </p>
              
              <!-- Product Details -->
              <div class="flex items-center justify-between mb-3">
                <div class="flex flex-col">
                  <span class="font-semibold text-gray-900 text-sm">
                    {{ formatCurrency(product.retailPrice) }}
                  </span>
                  <span v-if="product.salePrice && product.salePrice < product.retailPrice" 
                        class="text-xs text-gray-500 line-through">
                    {{ formatCurrency(product.salePrice) }}
                  </span>
                </div>
                
                <div class="text-right">
                  <span v-if="product.unit" class="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    {{ product.unit }}
                  </span>
                </div>
              </div>
              
              <!-- Product Meta -->
              <div class="flex items-center justify-between text-xs text-gray-500 mb-3">
                <span v-if="product.sku">{{ product.sku }}</span>
                <span>สินค้าฮาลาล</span>
              </div>
              
              <div class="flex items-center justify-between">
                <span class="text-xs text-gray-500">
                  คุณภาพมาตรฐาน
                </span>
                <button class="text-sm text-gray-700 hover:text-gray-900">
                  ดูรายละเอียด →
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- No Products Message -->
        <div v-else-if="!isLoading" class="text-center py-12">
          <p class="text-gray-500">ยังไม่มีผลิตภัณฑ์ในระบบ</p>
        </div>


      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useNuxtApp } from '#app'

// Props
const props = defineProps({
  clusterId: {
    type: String,
    default: null
  }
})

// Nuxt app
const { $DSS } = useNuxtApp()

// State management - ใช้ logic จาก products.vue
const products = ref([])
const isLoading = ref(true)
const error = ref(null)

// Search and filter states
const searchQuery = ref('')
const sortKey = ref('')
const sortDirection = ref('asc')
const currentPage = ref(1)
const itemsPerPage = ref(9) // แสดง 9 รายการล่าสุด

// Get nested value function จาก products.vue
const getNestedValue = (obj, path) => {
  return obj[path]
}

// Fetch products function - adapted จาก products.vue
const fetchProducts = async () => {
  isLoading.value = true
  error.value = null
  
  try {
    console.log('Fetching products for HalalSite...')
    const response = await $DSS('products')
    console.log('Products response:', response)
    
    if (response && response.success && Array.isArray(response.data)) {
      products.value = response.data
      console.log('Products loaded:', products.value)
    } else {
      throw new Error(response?.error || 'รูปแบบข้อมูลที่ได้รับไม่ถูกต้อง')
    }
  } catch (err) {
    console.error('เกิดข้อผิดพลาดในการดึงข้อมูลสินค้า:', err)
    error.value = err.message || 'ไม่สามารถโหลดรายการสินค้าได้'
    products.value = []
  } finally {
    isLoading.value = false
  }
}

// Latest products - แสดงแค่ 9 รายการล่าสุด
const filteredAndSortedProducts = computed(() => {
  if (!Array.isArray(products.value)) {
    return []
  }

  // เรียงตาม createdAt จากล่าสุดไปเก่าสุด และแสดงแค่ 9 รายการ
  let latestProducts = [...products.value].sort((a, b) => {
    const dateA = new Date(a.createdAt || a.updatedAt || 0)
    const dateB = new Date(b.createdAt || b.updatedAt || 0)
    return dateB - dateA // จากล่าสุดไปเก่าสุด
  })

  // แสดงแค่ 9 รายการแรก
  return latestProducts.slice(0, 9)
})

// แสดงผลิตภัณฑ์ล่าสุด 9 รายการ (ไม่ต้อง pagination)
const paginatedProducts = computed(() => {
  return filteredAndSortedProducts.value
})

// Utility functions จาก products.vue
const formatCurrency = (amount, currency = 'THB') => {
  if (amount === '' || amount === null || amount === undefined) {
    return 'N/A'
  }
  try {
    const numericAmount = parseFloat(amount)
    if (isNaN(numericAmount)) {
      return 'N/A'
    }
    return new Intl.NumberFormat('th-TH', { 
      style: 'currency', 
      currency: currency 
    }).format(numericAmount)
  } catch (e) {
    console.error('เกิดข้อผิดพลาดในการจัดรูปแบบสกุลเงิน:', e)
    return String(amount)
  }
}

// Get product image with fallback
const getProductImage = (product) => {
  if (product.mainImageUrl && product.mainImageUrl !== '') {
    return product.mainImageUrl
  }
  
  // Category-based fallback images
  const categoryImages = {
    'อาหาร': 'https://images.unsplash.com/photo-1565299624946-b28f40a0ca4b?w=400&h=400&fit=crop&crop=center&auto=format&q=80',
    'เครื่องดื่ม': 'https://images.unsplash.com/photo-1544145945-f90425340c7e?w=400&h=400&fit=crop&crop=center&auto=format&q=80',
    'เครื่องเทศ': 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=400&h=400&fit=crop&crop=center&auto=format&q=80',
    'ข้าว': 'https://images.unsplash.com/photo-1567620905732-2d1ec7ab7445?w=400&h=400&fit=crop&crop=center&auto=format&q=80',
    'default': 'https://images.unsplash.com/photo-1565299624946-b28f40a0ca4b?w=400&h=400&fit=crop&crop=center&auto=format&q=80'
  }
  
  return categoryImages[product.category] || categoryImages.default
}

// Handle image error
const handleImageError = (event) => {
  event.target.src = 'https://images.unsplash.com/photo-1565299624946-b28f40a0ca4b?w=400&h=400&fit=crop&crop=center&auto=format&q=80'
}

// Get discount percentage
const getDiscountPercentage = (product) => {
  if (!product.retailPrice || !product.salePrice || product.salePrice >= product.retailPrice) {
    return 0
  }
  
  const discount = ((product.retailPrice - product.salePrice) / product.retailPrice) * 100
  return Math.round(discount)
}

// Pagination functions
const changePage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page
  }
}

const getVisiblePages = () => {
  const total = totalPages.value
  const current = currentPage.value
  const visible = []
  
  if (total <= 7) {
    // Show all pages if 7 or fewer
    for (let i = 1; i <= total; i++) {
      visible.push(i)
    }
  } else {
    // Complex pagination logic
    visible.push(1)
    
    if (current > 4) {
      visible.push('...')
    }
    
    const start = Math.max(2, current - 1)
    const end = Math.min(total - 1, current + 1)
    
    for (let i = start; i <= end; i++) {
      visible.push(i)
    }
    
    if (current < total - 3) {
      visible.push('...')
    }
    
    if (total > 1) {
      visible.push(total)
    }
  }
  
  return visible
}

// Search and sort functions
const toggleSortDirection = () => {
  sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'
}

const clearSearch = () => {
  searchQuery.value = ''
}

// Watch for search query changes to reset pagination
watch(searchQuery, () => {
  currentPage.value = 1
})

watch(sortKey, () => {
  currentPage.value = 1
})

// Load products on mount
onMounted(() => {
  fetchProducts()
})
</script>

<style scoped>
/* Line clamp utilities */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Custom transitions */
.transition-all {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Hover effects */
.group:hover .group-hover\:scale-105 {
  transform: scale(1.05);
}

/* Custom scrollbar for product grid */
.product-grid {
  scrollbar-width: thin;
  scrollbar-color: rgba(16, 185, 129, 0.3) transparent;
}

.product-grid::-webkit-scrollbar {
  width: 6px;
}

.product-grid::-webkit-scrollbar-track {
  background: transparent;
}

.product-grid::-webkit-scrollbar-thumb {
  background-color: rgba(16, 185, 129, 0.3);
  border-radius: 3px;
}
</style>